package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.CreateHarvestReportRequest
import fr.frux.shared.dto.CreateHarvestRequest
import fr.frux.shared.dto.HarvestDto
import fr.frux.shared.dto.HarvestListResponse
import fr.frux.shared.dto.NearbyHarvestsResponse
import fr.frux.shared.dto.UpdateHarvestStatusRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import kotlin.uuid.Uuid

/**
 * API interface for harvest-related operations.
 */
interface HarvestApi {
    /**
     * Create a new harvest.
     *
     * [idempotencyKey] rejoue en `Idempotency-Key` (#280) : un défaut aléatoire par appel évite
     * la double création sur un rejeu réseau, un défaut explicite permet à l'appelant de
     * dédupliquer volontairement une nouvelle tentative après timeout.
     */
    suspend fun createHarvest(
        request: CreateHarvestRequest,
        idempotencyKey: String = Uuid.random().toString(),
    ): HarvestDto

    /**
     * Get all harvests for a specific tree.
     *
     * The server wraps the list in [HarvestListResponse] (harvests + count, envelope
     * ready for pagination); this method unwraps and returns the harvests.
     */
    suspend fun getHarvestsForTree(treeId: Long): List<HarvestDto>

    /** Récoltes d'un lieu (#617), même enveloppe que [getHarvestsForTree]. */
    suspend fun getHarvestsForSite(siteId: Long): List<HarvestDto>

    /**
     * Récoltes à venir dans le rayon [radius] du point (#166), même convention que
     * [TreeApi.getNearbyTrees] : `radius` en kilomètres.
     *
     * Rend la réponse complète (pas seulement `harvests`) : `total` porte le compte serveur, dont
     * la troncature du lot se déduit côté appelant (#155).
     */
    suspend fun getNearbyHarvests(
        lat: Double,
        lon: Double,
        radius: Double = 5.0,
        limit: Int = 20,
    ): NearbyHarvestsResponse

    /**
     * Get harvest by ID.
     */
    suspend fun getHarvestById(id: Long): HarvestDto

    /**
     * Join a harvest.
     */
    suspend fun joinHarvest(id: Long): HarvestDto

    /**
     * Leave a harvest.
     */
    suspend fun leaveHarvest(id: Long): HarvestDto

    /**
     * Update harvest status (organizer only).
     */
    suspend fun updateHarvestStatus(
        id: Long,
        request: UpdateHarvestStatusRequest,
    ): HarvestDto

    /** Signale la description de la récolte (#515, ADR 0029). 201 sans effet visible, 403
     *  `SELF_REPORT` pour l'organisateur, 409 `ALREADY_REPORTED`, 429 `REPORT_QUOTA_EXCEEDED`. */
    suspend fun reportHarvest(
        id: Long,
        kind: String = "INAPPROPRIATE_CONTENT",
    )
}

/**
 * Implementation of HarvestApi using Ktor HttpClient.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class HarvestApiImpl(
    private val httpClient: HttpClient,
) : HarvestApi {
    override suspend fun createHarvest(
        request: CreateHarvestRequest,
        idempotencyKey: String,
    ): HarvestDto =
        httpClient
            .post("${ApiPaths.V1}/harvests") {
                header("Idempotency-Key", idempotencyKey)
                setBody(request)
            }.body()

    override suspend fun getHarvestsForTree(treeId: Long): List<HarvestDto> {
        val response: HarvestListResponse =
            httpClient
                .get("${ApiPaths.V1}/harvests") {
                    parameter("treeId", treeId)
                }.body()
        return response.harvests
    }

    override suspend fun getHarvestsForSite(siteId: Long): List<HarvestDto> {
        val response: HarvestListResponse =
            httpClient
                .get("${ApiPaths.V1}/harvests") {
                    parameter("siteId", siteId)
                }.body()
        return response.harvests
    }

    override suspend fun getNearbyHarvests(
        lat: Double,
        lon: Double,
        radius: Double,
        limit: Int,
    ): NearbyHarvestsResponse =
        httpClient
            .get("${ApiPaths.V1}/harvests") {
                parameter("lat", lat)
                parameter("lon", lon)
                parameter("radius", radius)
                parameter("limit", limit)
            }.body()

    override suspend fun getHarvestById(id: Long): HarvestDto = httpClient.get("${ApiPaths.V1}/harvests/$id").body()

    override suspend fun joinHarvest(id: Long): HarvestDto = httpClient.post("${ApiPaths.V1}/harvests/$id/join").body()

    override suspend fun leaveHarvest(id: Long): HarvestDto {
        // Deux instructions à dessein : ktlint recolle un corps-expression sous ses 140 colonnes,
        // detekt plafonne à 120 (#237) — la ligne unique est invalide d'un côté ou de l'autre.
        val url = "${ApiPaths.V1}/harvests/$id/leave"
        return httpClient.post(url).body()
    }

    override suspend fun updateHarvestStatus(
        id: Long,
        request: UpdateHarvestStatusRequest,
    ): HarvestDto =
        httpClient
            .put("${ApiPaths.V1}/harvests/$id/status") {
                setBody(request)
            }.body()

    override suspend fun reportHarvest(
        id: Long,
        kind: String,
    ) {
        httpClient.post("${ApiPaths.V1}/harvests/$id/reports") {
            setBody(CreateHarvestReportRequest(kind = kind))
        }
    }
}
